import React, { useMemo, useState } from 'react';
import { Badge, Dropdown, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Envelope, SlashCircle, Trash, FileEarmark, FileEarmarkArrowDown } from 'react-bootstrap-icons';
import { canViewCollectedFees, isAdmin, isClient, isStaff } from '../../../../helpers/global';
import { computeCollectedFees } from '../../../../helpers/collectedFees';
import { getBgColorFromStatus } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import CircularProgressBar from '../../../circular-progress';
import SmallDropdownToggle from '../../../SmallDropdownToggle';
import {
  CLIENT_CANCELLED_INTERNAL_STATUSES,
  getClientFacingOrderStatus
} from '../../../../helpers/constants';
import { Calendar, Circle, DollarSign, Mail, User } from 'lucide-react';
import RushIconSVG from '../../../icons/RushIconSVG';
import FilePopup from '../../../common/FilePopup';
import EtaClickableValue from '../EtaClickableValue';
import OrderDetailField from '../OrderDetailField';
import OrderPinToggle from '../OrderPinToggle';
import CancellationRequestedBadge from '../CancellationRequestedBadge';

const commonRows = [
  {
    label: 'Assigned Processor',
    key: 'assignedProcessors',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  {
    label: 'Assigned Report Analyst',
    key: 'assignedReportAnalysts',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  { label: 'Hard Cost', key: 'hardCost', icon: <DollarSign className="icon-sm" /> },
  { label: 'Processing Fee', key: 'processingFee', icon: <DollarSign className="icon-sm" /> },
  { label: 'Collected Fees', key: 'collectedFees', icon: <DollarSign className="icon-sm" /> }
];

const commonRowsClient = [
  {
    label: 'Assigned Processor',
    key: 'assignedProcessors',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  {
    label: 'Processor Email',
    key: 'assignedProcessorEmails',
    isArray: true,
    objectKey: 'email',
    icon: <Mail className="icon-sm" />
  }
];

const priceFields = ['hardCost', 'totalCost', 'processingFee', 'collectedFees'];
const dateFields = ['dateDelivered', 'estimatedTurnAroundTime', 'neededDate', 'createdAt'];
const HARD_COST_REQUIRED_STATUSES = ['Information Requested', 'Ready for QA', 'Information Received'];

const resolveReportQaIssueId = order =>
  order?.reportQaIssue?._id || order?.reportQaIssue || null;

const LienSearchDetails = ({
  lienSearch: originalLienSearch,
  disabled,
  onStatusToggle,
  serviceCreation = false,
  service,
  onDeleteClick,
  onRequestCancellationClick,
  onUndoCancellationClick,
  onPaymentReminderClicked,
  hasPastDueUnpaidInvoices = false,
  latestReport,
  onEtaUpdate,
  onSyncOrderHardcost,
  loadingMetaData,
  onOrderFieldUpdate,
  onTogglePin,
  isPinning = false
}) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const currentUserInfo = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const canSeeCollectedFees = useMemo(() => canViewCollectedFees(currentUserInfo), [currentUserInfo]);
  const statuses = useAppChoices('Lien Search Status');
  const [previewFile, setPreviewFile] = useState(null);

  const lienSearch = useMemo(() => {
    const computed = computeCollectedFees(originalLienSearch, originalLienSearch.feeSourceInvoices);
    return {
      ...originalLienSearch,
      totalCost: ['hardCost', 'processingFee'].reduce((acc, field) => acc + (originalLienSearch[field] || 0), 0),
      collectedFees: computed,
      assignedProcessorEmails: originalLienSearch.assignedProcessors || []
    };
  }, [originalLienSearch]);

  const hasReportQaIssue = useMemo(() => Boolean(resolveReportQaIssueId(lienSearch)), [lienSearch]);

  const showPaymentReminderButton = () =>
    isAdmin(role) && lienSearch['status'] === 'Pending To Pay' && hasPastDueUnpaidInvoices;

  const fields = useMemo(() => {
    const baseRows = !isStaff(role)
      ? commonRowsClient
      : canSeeCollectedFees
        ? commonRows
        : commonRows.filter(r => r.key !== 'collectedFees');

    if (isClient(role) && lienSearch.assignedProcessors?.length > 0) return baseRows;
    return baseRows.filter(r => r.key !== 'assignedProcessorEmails');
  }, [role, canSeeCollectedFees, lienSearch.assignedProcessors]);

  const handleToggleStatus = status => {
    // TODO: Uncomment this after QA analysis is stable
    // if (status === 'Ready for QA' && !hasReportQaIssue) {
    //   toast.error('Run QA analysis in the Report Builder before marking Ready for QA.');
    //   return;
    // }
    onStatusToggle?.(status);
  };

  const isStatusUpdating = Boolean(loadingMetaData?.status);
  const isBusy = disabled || isStatusUpdating;

  return (
    <React.Fragment>
      <div className="card p-4 shadow-sm h-100">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="mb-2 mb-md-0">
            <div className="d-flex align-items-center">
              <OrderPinToggle
                order={originalLienSearch}
                disabled={isBusy}
                isPinning={isPinning}
                onTogglePin={onTogglePin}
              />
              <h1 className="font-weight-semibold xlargeFont mb-0">Lien Search </h1>
              <div className="d-flex align-items-center ml-2" style={{ gap: '8px' }}>
                {lienSearch?.rush && (
                  <span
                    style={{
                      padding: '1px 6px',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                    className="text-danger bg-danger-light rounded-pill"
                  >
                    RUSH
                  </span>                
                )}
                {lienSearch['cancellationRequested'] && (
                  <CancellationRequestedBadge
                    disabled={disabled}
                    hideUndo={CLIENT_CANCELLED_INTERNAL_STATUSES.includes(lienSearch['status'])}
                    onUndoClick={() => onUndoCancellationClick?.(lienSearch)}
                  />
                )}
              </div>
            </div>
            <div className="d-flex align-items-center text-muted midFont mt-1">
              Service No. {lienSearch['accurateId']}
            </div>
          </div>
          <div>
            {!serviceCreation && showPaymentReminderButton() && (
              <Button
                variant="outline-dark"
                disabled={disabled}
                className="ml-1 d-inline-flex align-items-center shadow-sm"
                onClick={() => onPaymentReminderClicked(lienSearch?._id)}
              >
                <Envelope size={13} className="mr-1" />
                Send Payment Reminder
              </Button>
            )}
            {!isClient(role) ? (
              <>
                <Button
                  variant="outline-success"
                  disabled={disabled}
                  className="ml-1 d-inline-flex align-items-center shadow-sm"
                  onClick={() =>
                    window.open(`/services/pipeline?serviceId=${service._id}&orderId=${lienSearch._id}`, '_blank')
                  }
                >
                  <FileEarmark size={13} className="mr-1" />
                  Open Report Builder
                </Button>
                <Button
                  variant="outline-danger"
                  className="ml-1 d-inline-flex align-items-center shadow-sm"
                  onClick={() => onDeleteClick(lienSearch)}
                  disabled={disabled}
                >
                  <Trash size={13} className="mr-1" />
                  Delete
                </Button>
              </>
            ) : (
              latestReport && (
                <Button
                  variant="outline-dark"
                  className="ml-1 d-inline-flex align-items-center shadow-sm"
                  onClick={() => setPreviewFile(latestReport)}
                  disabled={!latestReport}
                >
                  <FileEarmarkArrowDown size={13} className="mr-1" />
                  Download Final Report
                </Button>
              )
            )}
          </div>
        </div>

        {/* Details Section */}
        <div>
          {(() => {
            const allFields = [
              {
                label: 'Status',
                key: 'status',
                icon: <Circle fill="currentColor" className="icon-sm" />,
                isSpecial: true,
                render: () =>
                  isStaff(role) ? (
                    !serviceCreation && (
                      <Dropdown onClick={e => e.stopPropagation()} className="d-inline-block mr-1">
                        <OverlayTrigger
                          placement="bottom"
                          overlay={<Tooltip id={`tooltip-order-status-action`}>Change order status</Tooltip>}
                        >
                          <SmallDropdownToggle
                            variant=""
                            disabled={isBusy}
                            style={{ border: 0, backgroundColor: getBgColorFromStatus(lienSearch['status']).bg }}
                            size="sm"
                            aria-label="Change order status"
                          >
                            <span
                              className="d-inline-flex align-items-center largeFont font-weight-semibold p-1"
                              style={{ color: getBgColorFromStatus(lienSearch['status']).text }}
                            >
                              {isStatusUpdating ? (
                                <CircularProgressBar size={1} className="mr-2" />
                              ) : null}
                              {lienSearch['status']}
                            </span>
                          </SmallDropdownToggle>
                        </OverlayTrigger>

                        <Dropdown.Menu>
                          {statuses
                            ?.filter(
                              s => s !== 'Pending' && (s !== 'Revision' || ['Admin', 'Processor'].includes(role))
                            )
                            .map(s => {
                              const isReadyForQaDisabled = s === 'Ready for QA' && !hasReportQaIssue;
                              return (
                                <Dropdown.Item
                                  style={{
                                    backgroundColor: getBgColorFromStatus(s).bg,
                                    color: getBgColorFromStatus(s).text,
                                    opacity: isReadyForQaDisabled ? 0.5 : 1
                                  }}
                                  key={s}
                                  // TODO: Uncomment this after QA analysis is stable
                                  // disabled={isReadyForQaDisabled}
                                  onClick={() => handleToggleStatus(s)}
                                >
                                  {s}
                                </Dropdown.Item>
                              );
                            })}
                        </Dropdown.Menu>
                      </Dropdown>
                    )
                  ) : (
                    (() => {
                      const clientStatus = getClientFacingOrderStatus(
                        lienSearch['status'],
                        lienSearch['cancellationRequested']
                      );
                      const statusColors = getBgColorFromStatus(clientStatus);
                      return (
                        <Badge className="mr-1 p-2" style={{ backgroundColor: statusColors.bg }}>
                          <span className="midFont" style={{ color: statusColors.text }}>
                            {clientStatus === 'Completed/Unpaid' ? (
                              <>
                                Completed/<span className="text-danger">Unpaid</span>
                              </>
                            ) : (
                              clientStatus
                            )}
                          </span>
                        </Badge>
                      );
                    })()
                  )
              },
              {
                label: 'ETA',
                key: 'estimatedTurnAroundTime',
                icon: <Calendar className="icon-sm" />,
                labelWarningTooltipText:
                  lienSearch?.status === 'Update' && !lienSearch?.isEtaUpdatedAfterUpdateStatus
                    ? 'New ETA is required'
                    : undefined,
                isSpecial: true,
                render: () => (
                  <EtaClickableValue
                    value={lienSearch['estimatedTurnAroundTime']}
                    disabled={disabled}
                    loading={loadingMetaData.eta}
                    onDateChange={onEtaUpdate}
                    needsNewEta={lienSearch?.status === 'Update' && !lienSearch?.isEtaUpdatedAfterUpdateStatus}
                  />
                )
              },
              ...(!isClient(role) ? [
                {
                  label: 'Rush',
                  key: 'rush',
                  icon: <RushIconSVG width="18px" height="18px" />
                }
              ] : []),
              {
                label: 'Date Ordered',
                key: 'createdAt',
                icon: <Calendar className="icon-sm" />
              },
              ...(lienSearch['dateDelivered']
                ? [
                  {
                    label: 'Date Delivered',
                    key: 'dateDelivered',
                    icon: <Calendar className="icon-sm" />
                  }
                ]
                : []),
              ...fields
            ];

            const rows = [];
            for (let i = 0; i < allFields.length; i += 4) {
              const rowFields = allFields.slice(i, i + 4);
              rows.push(
                <div key={i} className="row mt-4">
                  {rowFields.map((field, index) => (
                    <OrderDetailField
                      key={field.key ?? `${i}-${index}`}
                      field={field}
                      order={lienSearch}
                      dateFields={dateFields}
                      priceFields={priceFields}
                      hardCostRequiredStatuses={HARD_COST_REQUIRED_STATUSES}
                      role={role}
                      currentUserInfo={currentUserInfo}
                      loadingMetaData={loadingMetaData}
                      onSyncOrderHardcost={onSyncOrderHardcost}
                      disabled={disabled}
                      onOrderFieldUpdate={onOrderFieldUpdate}
                      allowInlineEditing={!serviceCreation}
                    />
                  ))}
                </div>
              );
            }

            return rows;
          })()}
        </div>

        {isClient(role) &&
          !lienSearch?.['cancellationRequested'] &&
          !CLIENT_CANCELLED_INTERNAL_STATUSES.includes(lienSearch?.status) && (
          <div className="mt-3 d-flex justify-content-end">
            <Button
              variant="outline-danger"
              className="ml-1 d-inline-flex align-items-center shadow-sm"
              onClick={() => onRequestCancellationClick(lienSearch)}
              disabled={disabled}
            >
              <SlashCircle size={13} className="mr-1" />
              Request Order Cancellation
            </Button>
          </div>
        )}

        {previewFile && <FilePopup file={previewFile} onHide={() => setPreviewFile(null)} isDownloadable={true} />}
      </div>
    </React.Fragment>
  );
};

export default LienSearchDetails;
